package com.sweetlime.bingeamigo.repository

import com.sweetlime.bingeamigo.model.UserActivity
import com.sweetlime.bingeamigo.model.UserProfile
import com.sweetlime.bingeamigo.model.XpLogEntry
import com.sweetlime.bingeamigo.service.XpService
import dev.gitlive.firebase.firestore.FirebaseFirestore

class FirebaseUserRepository(
    private val authRepository: AuthRepository,
    private val firestore: FirebaseFirestore,
    private val xpService: XpService,
) : UserRepository {

    override suspend fun searchUsers(
        query: String
    ): List<UserProfile> {

        val q = query.lowercase()
        return firestore.collection("users")
            .where { "usernameLower" greaterThanOrEqualTo q }
            .limit(20)
            .get()
            .documents
            .mapNotNull { it.data<UserProfile>() }
            .filter { it.usernameLower.startsWith(q) }
    }

    override suspend fun followUser(
        targetUser: UserProfile
    ) {

        val currentUserId =
            authRepository.currentUserId()
                ?: return

        firestore.collection("users")
            .document(currentUserId)
            .collection("following")
            .document(targetUser.id)
            .set(mapOf("userId" to targetUser.id))

        firestore.collection("users")
            .document(targetUser.id)
            .collection("followers")
            .document(currentUserId)
            .set(
                mapOf(
                    "userId" to currentUserId
                )
            )

        xpService.awardXp(currentUserId, XpService.ACTION_FRIEND_ADDED, targetUser.id, XpService.XP_FRIEND_ADDED)
    }

    override suspend fun unfollowUser(
        targetUserId: String
    ) {

        val currentUserId =
            authRepository.currentUserId()
                ?: return

        firestore.collection("users")
            .document(currentUserId)
            .collection("following")
            .document(targetUserId)
            .delete()

        firestore.collection("users")
            .document(targetUserId)
            .collection("followers")
            .document(currentUserId)
            .delete()
    }

    override suspend fun isFollowing(
        targetUserId: String
    ): Boolean {
        val currentUserId =
            authRepository.currentUserId()
                ?: return false

        val doc =
            firestore.collection("users")
                .document(currentUserId)
                .collection("following")
                .document(targetUserId)
                .get()

        return doc.exists
    }

    override suspend fun createUserProfile(profile: UserProfile) {
        firestore.collection("users")
            .document(profile.id)
            .set(profile.copy(usernameLower = profile.username.lowercase()))
    }

    override suspend fun getFollowers(
        userId: String
    ): List<UserProfile> {

        return firestore.collection("users")
            .document(userId)
            .collection("followers")
            .get()
            .documents
            .mapNotNull { doc ->
                val followerId = doc.get("userId") as? String
                    ?: return@mapNotNull null
                getUserProfile(followerId)
            }
    }

    override suspend fun getFollowing(
        userId: String
    ): List<UserProfile> {

        return firestore.collection("users")
            .document(userId)
            .collection("following")
            .get()
            .documents
            .mapNotNull { doc ->
                val followingId = doc.get("userId") as? String
                    ?: return@mapNotNull null
                getUserProfile(followingId)
            }
    }

    override suspend fun getUserProfile(userId: String): UserProfile? {
        return try {
            firestore.collection("users")
                .document(userId)
                .get()
                .data<UserProfile>()
        } catch (e: Exception){
            return null
        }
    }

    override suspend fun updateUsername(userId: String, username: String) {
        firestore.collection("users")
            .document(userId)
            .update("username" to username, "usernameLower" to username.lowercase())
    }

    override suspend fun updateAvatar(userId: String, avatarUrl: String) {
        firestore.collection("users")
            .document(userId)
            .update("avatarUrl" to avatarUrl)
    }

    override suspend fun getUserActivity(userId: String): List<UserActivity> {
        return firestore.collection("users")
            .document(userId)
            .collection("activity")
            .get()
            .documents
            .map { it.data<UserActivity>() }
    }

    override suspend fun getFollowingIds(userId: String): List<String> {
        return runCatching {
            firestore.collection("users")
                .document(userId)
                .collection("following")
                .get()
                .documents
                .mapNotNull { doc -> doc.get("userId") as? String }
        }.getOrDefault(emptyList())
    }

    override suspend fun getXpLog(userId: String): List<XpLogEntry> {
        return runCatching {
            firestore.collection("users")
                .document(userId)
                .collection("xpLog")
                .get()
                .documents
                .map { it.data<XpLogEntry>() }
        }.getOrDefault(emptyList())
    }

}